import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, AlertCircle } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Pagination,
  Button,
  Skeleton,
  useAuth,
  UI_CONFIG,
} from '@aaas/shared';
import { Space } from '../types';
import { useSpaceListQuery } from '../hooks/useSpaceListQuery';
import { SpaceCard } from '../components/SpaceCard';

const SpaceListPage = () => {
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(UI_CONFIG.PAGINATION.DEFAULT_PAGE_SIZE);

  // Calculate skip for backend (0-based offset)
  const skip = (currentPage - 1) * pageSize;

  // React Query for space list with 30-second polling (pauses when tab hidden)
  const {
    data: spaceResponse,
    isLoading: loading,
    error,
  } = useSpaceListQuery({
    idToken: idToken || undefined,
    skip,
    limit: pageSize,
    enabled: !!user && !!currentWorkspace,
    refetchInterval: 30000, // Poll every 30 seconds
  });

  // Extract data from response
  const spaces = spaceResponse?.data?.items || [];
  const totalItems = spaceResponse?.data?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize) || 0;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Handle edit space
  const handleEdit = (space: Space) => {
    navigate(`/kms/spaces/${space.id}/edit`);
  };

  // SpaceList Skeleton Component
  const SpaceListSkeleton = () => (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton variant="text" className="w-32 h-8" />
        <Skeleton variant="rectangular" className="w-32 h-10" />
      </div>

      {/* Cards Grid Skeleton */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {Array.from({ length: Math.min(pageSize, 8) }).map((_, index) => (
          <div key={index} className="bg-white rounded-lg border shadow-sm p-6">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton variant="text" className="w-32 h-5" />
                <Skeleton variant="rectangular" className="w-16 h-6" />
              </div>
              <Skeleton variant="text" className="w-full h-4" />
              <Skeleton variant="text" className="w-3/4 h-4" />
              <div className="flex justify-between">
                <Skeleton variant="text" className="w-20 h-3" />
                <Skeleton variant="text" className="w-20 h-3" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Pagination Skeleton */}
      <div className="flex justify-between items-center">
        <Skeleton variant="text" className="w-32 h-4" />
        <div className="flex gap-2">
          <Skeleton variant="rectangular" className="w-8 h-8" />
          <Skeleton variant="rectangular" className="w-8 h-8" />
          <Skeleton variant="rectangular" className="w-8 h-8" />
        </div>
        <Skeleton variant="rectangular" className="w-24 h-8" />
      </div>
    </div>
  );

  if (loading) {
    return <SpaceListSkeleton />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Spaces</h1>

        {spaces.length > 0 && (
          <Button
            onClick={() => navigate('/kms/spaces/create')}
            className="bg-green-600 hover:bg-green-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Space
          </Button>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load spaces'}
          </AlertDescription>
        </Alert>
      )}

      {/* Space Cards Grid */}
      <div className="space-y-6">
        {spaces.length === 0 && !loading ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-4">No spaces found</div>
            <Button
              onClick={() => navigate('/kms/spaces/create')}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create your first space
            </Button>
          </div>
        ) : (
          <>
            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {spaces.map((space) => (
                <SpaceCard
                  key={space.id}
                  space={space}
                  onEdit={handleEdit}
                />
              ))}
            </div>

            {/* Pagination - Only show if more than 1 page */}
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[...UI_CONFIG.PAGINATION.PAGE_SIZE_OPTIONS]}
                showTotal={true}
                showPageSizeSelector={true}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SpaceListPage;
